import { useState, useEffect } from "react";
import { NavLink, Link } from "react-router-dom";
import {
  LayoutDashboard,
  School,
  Users,
  BarChart2,
  CreditCard,
  ChevronLeft,
  ChevronRight,
  Moon,
  Sun,
} from "lucide-react";
import { UserButton, useUser } from "@clerk/clerk-react";
import useSubscriptionInfo from "@/hooks/useSubscriptionInfo";

const navItems = [
  { name: "Dashboard", path: "/dashboard", icon: LayoutDashboard },
  { name: "My Classes", path: "/classes", icon: School },
  { name: "Shared Classes", path: "/shared-classes", icon: Users },
  { name: "Usage", path: "/usage", icon: BarChart2 },
  { name: "Billing", path: "/billing", icon: CreditCard },
];

const planBadgeStyle = {
  free: { bg: "rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.7)" },
  standard: { bg: "rgba(120,193,138,0.25)", color: "#a3d9b1" },
  professional: { bg: "rgba(96,165,250,0.2)", color: "#93c5fd" },
};

export default function Sidebar({ collapsed, onToggle }) {
  const { user } = useUser();
  const { planName, loading } = useSubscriptionInfo();
  const [theme, setTheme] = useState(localStorage.getItem("theme") || "light");

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
  }, [theme]);

  const handleThemeToggle = () => {
    const next = theme === "light" ? "dark" : "light";
    localStorage.setItem("theme", next);
    setTheme(next);
  };

  const badge = planBadgeStyle[planName] || planBadgeStyle.free;
  const planLabel = planName
    ? planName.charAt(0).toUpperCase() + planName.slice(1)
    : "Free";

  return (
    <aside className={`app-sidebar${collapsed ? " collapsed" : ""}`}>
      {/* Header */}
      <div className="sidebar-header">
        {!collapsed && (
          <Link to="/dashboard" className="sidebar-brand">
            <img src="/images/easyreports_logo.png" alt="EasyReports logo" style={{ height: 20, width: 20, objectFit: "contain", flexShrink: 0 }} />
            <span>EasyReports</span>
          </Link>
        )}
        <button
          className="sidebar-collapse-btn"
          onClick={onToggle}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          style={collapsed ? { margin: "0 auto" } : {}}
        >
          {collapsed ? <ChevronRight size={15} /> : <ChevronLeft size={15} />}
        </button>
      </div>

      {/* Navigation */}
      <nav className="sidebar-nav">
        {!collapsed && <span className="sidebar-nav-label">Navigation</span>}
        {navItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.path === "/dashboard"}
            className={({ isActive }) =>
              `sidebar-nav-item${isActive ? " active" : ""}`
            }
            title={collapsed ? item.name : undefined}
          >
            <item.icon size={18} className="sidebar-nav-icon" />
            {!collapsed && <span>{item.name}</span>}
          </NavLink>
        ))}
      </nav>

      {/* Footer */}
      <div className="sidebar-footer">
        {!collapsed && !loading && (
          <div className="sidebar-user-info">
            <span className="sidebar-user-name">
              {user?.firstName || user?.fullName || "Teacher"}
            </span>
            <span
              className="sidebar-plan-badge"
              style={{ background: badge.bg, color: badge.color }}
            >
              {planLabel} Plan
            </span>
          </div>
        )}
        <div className="sidebar-footer-actions">
          <button
            className="sidebar-icon-btn"
            onClick={handleThemeToggle}
            title="Toggle theme"
          >
            {theme === "light" ? <Moon size={15} /> : <Sun size={15} />}
          </button>
          <UserButton afterSignOutUrl="/" />
        </div>
      </div>
    </aside>
  );
}
